import { isCompanyRegistrationApprovalEnabled } from '../config/featureFlags.ts';
import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Check,
  Loader2,
  Sun,
  Moon,
  LogOut,
  UserRound,
  type LucideIcon
} from 'lucide-react';

import {
  getClinicInfo,
  getBillingSummary,
  getUserClinics,
  selectActiveClinic,
  getPendingNotifications,
  getMyTeamPermissions,
  type BillingAlert,
  type TaskNotification
} from '../services/api.ts';
import { useUserPermissions } from '../hooks/useUserPermissions.ts';
import { filterMenusByPermissions, setUserPermissions } from '../services/permissionService.ts';
import {
  buildMobileMenuItems,
  buildSidebarMenuSections,
  type SidebarMenuItem as PermissionMenuItem,
  type SidebarMenuSection,
  type SidebarSubItem as SubItem,
} from '../config/sidebarMenu.tsx';
import TaskNotifications from './TaskNotifications.tsx';
import LogoutConfirmModal from './LogoutConfirmModal.tsx';
import { messageCacheManager, unifiedWebSocketManager } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { BetaTag, NewTag } from './ui/StatusTags.tsx';
import { useWhatsAppStatus, type ConnectionStatus } from '../hooks/useWhatsAppStatus.ts';
import WhatsAppStatusModal from './WhatsAppStatusModal.tsx';
import PlanTag from './PlanTag.tsx';
import BillingTopBanner from './BillingTopBanner.tsx';

// Types
interface ClinicInfo {
  id?: number;
  name?: string;
  logo_url?: string | null;
  name_clinic?: string | null;
  plan?: {
    id: number;
    name: string;
    price: number;
    features: any;
  } | null;
}

interface ClinicListItem {
  clinic_id: number;
  name_clinic?: string;
  logo_url?: string | null;
  plan?: {
    id: number;
    name: string;
    price: number;
    features: any;
  } | null;
}

interface MenuItem extends PermissionMenuItem {
  // Extending from PermissionMenuItem to include permission field
}

// interface StatusData removida - não mais necessária

// Component Props Types

interface MenuItemProps {
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  isCollapsed: boolean;
  hasSubItems?: boolean;
  onClick: (e: React.MouseEvent) => void;
  isSubmenuOpen: boolean;
  badge?: string;
  status?: ConnectionStatus;
  isNew?: boolean;
  isBeta?: boolean;
}

interface SubMenuItemProps {
  label: string;
  isActive: boolean;
  path: string;
  icon?: LucideIcon;
  isNew?: boolean;
  isBeta?: boolean;
}

interface ClinicSelectorProps {
  isCollapsed: boolean;
  clinicLogoUrl: string | null;
  clinicName: string | null;
  clinicId: string | null;
  canSwitchClinics: boolean;
  showClinicDropdown: boolean;
  handleClinicBoxClick: () => void;
}

interface ClinicDropdownProps {
  isLoadingClinics: boolean;
  userClinics: ClinicListItem[];
  clinicId: string | null;
  handleSelectClinic: (id: number) => void;
}

interface MobileNavItemProps {
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  path: string;
  status?: ConnectionStatus;
}



// Components

const MenuItem: React.FC<MenuItemProps> = ({
  icon: Icon,
  label,
  isActive,
  isCollapsed,
  hasSubItems,
  onClick,
  isSubmenuOpen,
  badge,
  status,
  isNew,
  isBeta,
}) => {
  const { isDark } = useTheme();


  // Status dot color helper (collapsed mode)
  const statusDotClass =
    status === 'connected' ? 'bg-sminy-success animate-pulse'
    : status === 'disconnected' ? 'bg-red-500'
    : status === 'connecting' ? 'bg-amber-500 animate-pulse'
    : status === 'qr' ? 'bg-blue-500 animate-pulse'
    : '';

  // Status pill (expanded mode)
  const statusPill = status && (
    <span className={`flex items-center gap-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${
      status === 'connected'
        ? isDark ? 'bg-sminy-success/15 text-sminy-success' : 'bg-sminy-success/10 text-sminy-success'
        : status === 'disconnected'
          ? isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-700'
          : status === 'connecting'
            ? isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-700'
            : isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-700'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${statusDotClass}`}></span>
      {status === 'connected' ? 'on' : status === 'disconnected' ? 'off' : status === 'connecting' ? '...' : 'qr'}
    </span>
  );

  if (isCollapsed) {
    return (
      <div>
        <div className={`
          group relative cursor-pointer transition-all duration-150
          w-10 h-10 mx-auto rounded-lg flex items-center justify-center
          ${isActive
            ? isDark
              ? 'bg-sminy-primary/15 text-sminy-primary'
              : 'bg-sminy-blue-light text-sminy-primary'
            : isDark
              ? 'text-sminy-muted hover:bg-sminy-blue-soft/10 hover:text-white'
              : 'text-sminy-muted hover:bg-sminy-blue-soft hover:text-sminy-text-primary'
          }
        `}>
          <Icon className="w-5 h-5" />

          {statusDotClass && (
            <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${statusDotClass} ${
              isDark ? 'border-sminy-navy' : 'border-white'
            }`}></div>
          )}

          {badge && (
            <div className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-sminy-primary text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
              {badge}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`
        group relative flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer
        transition-all duration-150
        ${isActive
          ? isDark
            ? 'bg-sminy-primary/15 text-sminy-primary'
            : 'bg-sminy-blue-light text-sminy-primary'
          : isDark
            ? 'text-sminy-blue-soft hover:bg-sminy-blue-soft/10 hover:text-white'
            : 'text-sminy-text-secondary hover:bg-sminy-blue-soft hover:text-sminy-text-primary'
        }
      `}>
        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${
          isActive
            ? 'text-sminy-primary'
            : isDark ? 'text-sminy-muted group-hover:text-white' : 'text-sminy-muted group-hover:text-sminy-text-secondary'
        }`} />

        <span className="text-[13px] font-medium flex-1 truncate">{label}</span>

        {isNew && <NewTag />}
        {isBeta && <BetaTag />}

        {statusPill}

        {badge && (
          <div className="bg-sminy-primary text-white text-[10px] px-1.5 py-0.5 rounded-md font-semibold">
            {badge}
          </div>
        )}

        {hasSubItems && (
          <button
            onClick={onClick}
            className="p-0.5 rounded transition-colors duration-150"
            aria-label="Expandir submenu"
          >
            <ChevronDown className={`
              w-4 h-4 text-sminy-muted
              transition-transform duration-200
              ${isSubmenuOpen ? 'rotate-180' : ''}
            `} />
          </button>
        )}
      </div>
    </div>
  );
};

const SubMenuItem: React.FC<SubMenuItemProps> = ({
  label,
  isActive,
  path,
  icon: SubIcon,
  isNew,
  isBeta
}) => {
  const { isDark } = useTheme();
  return (
    <Link
      to={path}
      className={`
      flex items-center gap-2.5 pl-9 pr-3 py-1.5 rounded-md transition-all duration-150 group/sub
      ${isActive
          ? isDark
            ? 'bg-sminy-primary/15 text-sminy-primary'
            : 'bg-sminy-blue-soft text-sminy-primary'
          : isDark
            ? 'text-sminy-muted hover:bg-sminy-blue-soft/10 hover:text-white'
            : 'text-sminy-text-secondary hover:bg-sminy-blue-soft hover:text-sminy-text-primary'
        }
    `}
    >
      {SubIcon ? (
        <SubIcon className={`w-[14px] h-[14px] flex-shrink-0 ${isActive
          ? 'text-sminy-primary'
          : isDark
            ? 'text-sminy-muted'
            : 'text-sminy-muted group-hover/sub:text-sminy-text-secondary'
          }`} />
      ) : (
        <div className={`
        w-1.5 h-1.5 rounded-full transition-all duration-150 flex-shrink-0
        ${isActive
            ? 'bg-sminy-primary'
            : isDark
              ? 'bg-sminy-muted group-hover/sub:bg-sminy-blue-soft'
              : 'bg-sminy-border group-hover/sub:bg-sminy-muted'
          }
      `}></div>
      )}

      <span className="text-[12.5px] font-medium flex-1 truncate">{label}</span>

      {isNew && <NewTag />}
      {isBeta && <BetaTag />}
    </Link>
  );
};

interface MenuSectionProps {
  label: string;
  isCollapsed: boolean;
  children: React.ReactNode;
}

const MenuSection: React.FC<MenuSectionProps> = ({ label, isCollapsed, children }) => {
  const { isDark } = useTheme();

  return (
    <div className={isCollapsed ? 'mb-2' : 'mb-5'}>
      {!isCollapsed && (
        <h3 className={`text-[10.5px] font-semibold uppercase tracking-[0.1em] mb-2 px-3 ${
          isDark ? 'text-sminy-muted' : 'text-sminy-muted'
        }`}>
          {label}
        </h3>
      )}
      <ul className={isCollapsed ? 'space-y-1' : 'space-y-0.5'}>
        {children}
      </ul>
    </div>
  );
};

interface MenuGroupProps {
  label: string;
  isCollapsed: boolean;
  children: React.ReactNode;
}

const MenuGroup: React.FC<MenuGroupProps> = ({ label, isCollapsed, children }) => {
  const { isDark } = useTheme();
  const borderClass = isCollapsed
    ? 'border-t-0'
    : `border-t ${isDark ? 'border-sminy-border/15' : 'border-sminy-border'}`;

  return (
    <li className="!mt-6 list-none">
      <div className={`pt-4 ${borderClass}`}>
        {!isCollapsed && (
          <h4 className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-sminy-muted">
            {label}
          </h4>
        )}
        <ul className={isCollapsed ? 'space-y-1' : 'space-y-0.5'}>
          {children}
        </ul>
      </div>
    </li>
  );
};

const ClinicSelector: React.FC<ClinicSelectorProps> = ({
  isCollapsed,
  clinicLogoUrl,
  clinicName,
  clinicId,
  canSwitchClinics,
  showClinicDropdown,
  handleClinicBoxClick
}) => {
  const { isDark } = useTheme();

  // Helper para obter informações do plano do localStorage
  const getClinicPlan = () => {
    try {
      const planData = localStorage.getItem('clinic_plan');
      return planData ? JSON.parse(planData) : null;
    } catch (error) {
      console.error("Erro ao obter plano do localStorage:");
      return null;
    }
  };
  return (
    <button
      type="button"
      onClick={canSwitchClinics ? handleClinicBoxClick : undefined}
      disabled={!canSwitchClinics}
      aria-expanded={canSwitchClinics ? showClinicDropdown : undefined}
      className={`
      w-full transition-all duration-150 group
      ${isCollapsed ? 'flex justify-center py-2' : 'flex items-center gap-3 px-3 py-3'}
      ${canSwitchClinics ? (isDark ? 'hover:bg-sminy-blue-soft/10' : 'hover:bg-sminy-blue-soft') : 'cursor-default'}
    `}
    >
      {isCollapsed ? (
        <div className="relative mx-auto">
          <div className="w-9 h-9 bg-sminy-primary rounded-lg flex items-center justify-center transition-all duration-150">
            {clinicLogoUrl ? (
              <img
                src={clinicLogoUrl}
                alt="Clinic Logo"
                className="w-9 h-9 rounded-lg object-cover"
              />
            ) : (
              <Building2 className="w-5 h-5 text-white" />
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="relative flex-shrink-0">
            {clinicLogoUrl ? (
              <img
                src={clinicLogoUrl}
                alt="Clinic Logo"
                className={`w-9 h-9 rounded-lg object-cover ${isDark ? 'border border-sminy-border/15' : 'border border-sminy-border'}`}
              />
            ) : (
              <div className="w-9 h-9 bg-sminy-primary rounded-lg flex items-center justify-center">
                <Building2 className="w-[18px] h-[18px] text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 text-left">
            <p className={`font-semibold truncate text-[13px] ${isDark ? 'text-white' : 'text-sminy-text-primary'
              }`}>
              {clinicName || 'Sem Nome'}
            </p>
            <div className="flex items-center gap-1.5">
              {(() => {
                const plan = getClinicPlan();
                return plan ? (
                  <PlanTag
                    planName={plan.name}
                    price={plan.price}
                    size="small"
                  />
                ) : (
                  <span className={`text-[11px] ${isDark ? 'text-sminy-muted' : 'text-sminy-muted'}`}>
                    ID: {clinicId || '??'}
                  </span>
                );
              })()}
            </div>
          </div>

          {canSwitchClinics && (
            <ChevronDown className={`
            w-4 h-4 transition-transform duration-200 flex-shrink-0
            ${showClinicDropdown ? 'rotate-180' : 'rotate-0'}
            ${isDark ? 'text-sminy-muted' : 'text-sminy-muted'}
          `} />
          )}
        </>
      )}
    </button>
  );
};

const ClinicDropdown: React.FC<ClinicDropdownProps> = ({
  isLoadingClinics,
  userClinics,
  clinicId,
  handleSelectClinic
}) => {
  const { isDark } = useTheme();

  // Helper para obter informações do plano do localStorage
  const getClinicPlan = () => {
    try {
      const planData = localStorage.getItem('clinic_plan');
      const plan = planData ? JSON.parse(planData) : null;
      return plan;
    } catch (error) {
      console.error("Erro ao obter plano do localStorage:");
      return null;
    }
  };
  return (
    <div className={`absolute top-full left-0 w-full shadow-xl rounded-b-lg overflow-hidden duration-200 z-[70] ${isDark
      ? 'bg-sminy-navy border border-sminy-border/15'
      : 'bg-white border border-sminy-border'
      }`}>
      <div className={`px-3 py-2.5 border-b ${isDark
        ? 'border-sminy-border/15'
        : 'bg-sminy-blue-soft border-sminy-border'
        }`}>
        <h3 className={`text-[10.5px] font-semibold uppercase tracking-[0.1em] ${isDark ? 'text-sminy-muted' : 'text-sminy-muted'
          }`}>
          Suas Clínicas
        </h3>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {isLoadingClinics ? (
          <div className="flex items-center justify-center p-6">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 text-sminy-primary animate-spin" />
              <p className={`text-sm ${isDark ? 'text-sminy-muted' : 'text-sminy-text-secondary'
                }`}>Carregando clínicas...</p>
            </div>
          </div>
        ) : userClinics.length === 0 ? (
          <div className="p-6 text-center">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${isDark ? 'bg-sminy-blue-soft/10' : 'bg-sminy-blue-soft'
              }`}>
              <Building2 className={`w-6 h-6 ${isDark ? 'text-sminy-muted' : 'text-sminy-muted'
                }`} />
            </div>
            <p className={`text-sm ${isDark ? 'text-sminy-muted' : 'text-sminy-text-secondary'
              }`}>Nenhuma clínica encontrada</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-sminy-muted' : 'text-sminy-muted'
              }`}>Entre em contato com o suporte</p>
          </div>
        ) : (
          <div className="py-1">
            {userClinics.map((clinic) => {
              return (
                <button
                  key={clinic.clinic_id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectClinic(clinic.clinic_id);
                  }}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 transition-colors duration-150 group cursor-pointer ${isDark ? 'hover:bg-sminy-blue-soft/10' : 'hover:bg-sminy-blue-soft'
                    }`}
                  type="button"
                >
                  <div className="relative">
                    {clinic.logo_url && clinic.logo_url.trim() !== "" ? (
                      <img
                        src={clinic.logo_url}
                        alt={`Logo ${clinic.name_clinic || `Clínica ${clinic.clinic_id}`}`}
                        className="w-8 h-8 rounded-md object-cover border border-sminy-border shadow-sm"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = '/api/placeholder/32/32';
                        }}
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isDark
                        ? 'bg-sminy-blue-soft/10 border border-sminy-border/20'
                        : 'bg-sminy-blue-soft border border-sminy-border'
                        }`}>
                        <Building2 className={`w-4 h-4 ${isDark ? 'text-sminy-muted' : 'text-sminy-muted'
                          }`} />
                      </div>
                    )}
                    {clinic.clinic_id.toString() === clinicId && (
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-sminy-success border border-white rounded-full"></div>
                    )}
                  </div>

                  <div className="flex-1 text-left">
                    <p className={`font-medium group-hover:text-sminy-primary transition-colors duration-200 text-sm ${isDark ? 'text-sminy-blue-soft' : 'text-sminy-text-primary'
                      }`}>
                      {clinic.name_clinic || `Clínica ${clinic.clinic_id}`}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className={`text-xs ${isDark ? 'text-sminy-muted' : 'text-sminy-text-secondary'
                        }`}>ID: {clinic.clinic_id}</p>
                      {clinic.plan && (
                        <>
                          <span className={`w-px h-3 ${isDark ? 'bg-sminy-border/20' : 'bg-sminy-border'
                            }`}></span>
                          <PlanTag
                            planName={clinic.plan.name}
                            price={clinic.plan.price}
                            size="small"
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {clinicId === clinic.clinic_id.toString() && (
                    <div className={`p-1 rounded-md ${isDark
                      ? 'bg-sminy-success/20'
                      : 'bg-sminy-success/10'
                      }`}>
                      <Check className={`w-3 h-3 ${isDark ? 'text-sminy-success' : 'text-sminy-success'
                        }`} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};



const MobileNavItem: React.FC<MobileNavItemProps> = ({
  icon: Icon,
  label,
  isActive,
  path,
  status
}) => {
  const { isDark } = useTheme();
  return (
    <Link
      to={path}
      className={`
      flex flex-col items-center justify-center py-2 px-1 transition-all duration-200 relative
      ${isActive
          ? 'text-sminy-primary'
          : isDark
            ? 'text-sminy-muted hover:text-sminy-blue-soft'
            : 'text-sminy-muted hover:text-sminy-text-secondary'
        }
    `}
    >
      <div className="relative">
        <Icon className={`w-6 h-6 ${isActive
          ? 'text-sminy-primary'
          : isDark
            ? 'text-sminy-muted'
            : 'text-sminy-muted'
          }`} />
        {status === 'connected' && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-sminy-success rounded-full border border-white animate-pulse"></div>
        )}
        {status === 'disconnected' && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></div>
        )}
        {status === 'connecting' && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full border border-white animate-pulse"></div>
        )}
        {status === 'qr' && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full border border-white animate-pulse"></div>
        )}
      </div>
      <span className={`text-xs font-medium mt-1 ${isActive
        ? 'text-sminy-primary'
        : isDark
          ? 'text-sminy-muted'
          : 'text-sminy-muted'
        }`}>
        {label}
      </span>
      {isActive && (
        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-sminy-primary rounded-full"></div>
      )}
    </Link>
  );
};

// Main Component
const ProtectedLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const { toggleTheme, isDark } = useTheme();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [clinicName, setClinicName] = useState<string | null>(null);
  const [clinicLogoUrl, setClinicLogoUrl] = useState<string | null>(null);
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [userClinics, setUserClinics] = useState<ClinicListItem[]>([]);
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [websocketMessage, setWebsocketMessage] = useState<any>(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [billingAlert, setBillingAlert] = useState<BillingAlert | null>(null);
  // O backend devolve somente as clínicas vinculadas ao usuário operacional;
  // a sessão confirmada é a fonte de verdade para habilitar o seletor.
  const canSwitchClinics = session?.userType === 'master' || session?.userType === 'user';

  useEffect(() => {
    const invalidateClinics = () => { setUserClinics([]); setShowClinicDropdown(false); };
    window.addEventListener('sminy:clinic-memberships-changed', invalidateClinics);
    return () => window.removeEventListener('sminy:clinic-memberships-changed', invalidateClinics);
  }, []);


  // Hook para status do WhatsApp
  const { connectionStatus: whatsappConnectionStatus, loading: whatsappLoading, isMaintenanceMode } = useWhatsAppStatus();

  // Garantir que status null vire 'disconnected' para o menu
  const whatsappStatusForMenu = whatsappConnectionStatus || 'disconnected';


  useEffect(() => {
    const storedClinicId = localStorage.getItem('clinic_id');
    if (storedClinicId) {
      setClinicId(storedClinicId);
    }

    const fetchClinicInfo = async () => {
      try {
        const info = await getClinicInfo();
        const finalName = info.name_clinic?.trim() || info.name?.trim() || null;
        const finalLogo = info.logo_url?.trim() || null;

        if (info.id) {
          const nextClinicId = info.id.toString();
          localStorage.setItem('clinic_id', nextClinicId);
          setClinicId(nextClinicId);
        }

        setClinicName(finalName);
        setClinicLogoUrl(finalLogo);

        // Armazenar informações do plano no localStorage
        if (info.plan) {
          localStorage.setItem('clinic_plan', JSON.stringify(info.plan));
        } else {
          // Limpar plano se não existir
          localStorage.removeItem('clinic_plan');
        }
      } catch (error) {
        console.error("Erro ao obter informações da clínica:");
        setClinicName(null);
        setClinicLogoUrl(null);
      }
    };

    const fetchInitialNotifications = async () => {
      try {
        const notifications = await getPendingNotifications();
        if (notifications) {
          if (notifications.type !== 'none' || (notifications.human_handoff_tasks?.length ?? 0) > 0) {
            console.log("[ProtectedLayout] Notificações iniciais encontradas:");
          }
          setWebsocketMessage(notifications);
        }
      } catch (error) {
        console.error("Erro ao buscar notificações iniciais:");
      }
    };

    fetchClinicInfo();
    fetchInitialNotifications();
    // fetchWhatsAppStatus(); // Removido para reduzir carga no pool de conexões

    // Verificar status do WhatsApp a cada 30 segundos
    // const interval = setInterval(fetchWhatsAppStatus, 30000); // Removido para reduzir carga no pool de conexões

    // return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchBillingAlert = async () => {
      try {
        const summary = await getBillingSummary(false);
        if (!isMounted) {
          return;
        }

        const localAlert = summary.billing_alert || null;
        setBillingAlert(localAlert);

        if (!localAlert) {
          return;
        }

        try {
          const syncedSummary = await getBillingSummary(true);
          if (isMounted) {
            setBillingAlert(syncedSummary.billing_alert || null);
          }
        } catch (syncError) {
          console.warn("[ProtectedLayout] Não foi possível sincronizar alerta de cobrança:");
        }
      } catch (error) {
        console.warn("[ProtectedLayout] Não foi possível carregar alerta de cobrança:");
        if (isMounted) {
          setBillingAlert(null);
        }
      }
    };

    fetchBillingAlert();

    return () => {
      isMounted = false;
    };
  }, [clinicId]);

  // Função para verificar se deve mostrar popup
  const shouldShowWhatsAppModal = () => {
    // 🆕 NÃO mostrar popup se estiver em modo manutenção (restart detectado)
    if (isMaintenanceMode) {
      console.log("🛠️ [ProtectedLayout] Popup suprimido - modo manutenção detectado");
      return false;
    }

    // Não mostrar popup se já estiver na página de configuração do WhatsApp
    if (location.pathname === '/whatsapp' || location.pathname === '/canais') {
      console.log("🚫 [ProtectedLayout] Popup suprimido - usuário já está na página /whatsapp");
      return false;
    }

    if (whatsappConnectionStatus !== 'disconnected' || whatsappLoading) {
      return false;
    }

    // Verificar se usuário escolheu "Lembrar Depois"
    const remindLater = localStorage.getItem('whatsapp-remind-later');
    if (!remindLater) return true;

    const remindUntil = parseInt(remindLater);
    return Date.now() > remindUntil;
  };

  // Efeito para mostrar popup de desconexão do WhatsApp
  useEffect(() => {
    if (shouldShowWhatsAppModal()) {
      console.log("📱 [ProtectedLayout] WhatsApp desconectado, mostrando popup...");
      setShowWhatsAppModal(true);
    } else if (whatsappConnectionStatus === 'connected') {
      // Fechar popup quando conectar
      setShowWhatsAppModal(false);
    }
  }, [whatsappConnectionStatus, whatsappLoading, isMaintenanceMode]);

  // Efeito para verificar status ao mudar de página
  useEffect(() => {
    // Verificar status do WhatsApp sempre que mudar de rota
    if (shouldShowWhatsAppModal()) {
      console.log("🔄 [ProtectedLayout] Mudança de página detectada, WhatsApp desconectado");
      // Dar um pequeno delay para não ser muito intrusivo
      const timer = setTimeout(() => {
        setShowWhatsAppModal(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, whatsappConnectionStatus, whatsappLoading, isMaintenanceMode]);

  // Efeito para mostrar popup ao fazer login (carregamento inicial)
  useEffect(() => {
    // Verificar após carregamento inicial da página
    const timer = setTimeout(() => {
      if (shouldShowWhatsAppModal()) {
        console.log("👤 [ProtectedLayout] Login detectado, verificando WhatsApp...");
        setShowWhatsAppModal(true);
      }
    }, 2000); // Esperar 2 segundos após carregar a página

    return () => clearTimeout(timer);
  }, []); // Executar apenas uma vez no carregamento

  // WebSocket connection for real-time notifications
  useEffect(() => {
    // Conectar ao WebSocket
    unifiedWebSocketManager.connect();

    // Handler para mensagens globais usando onMessage
    const unsubscribeGlobal = unifiedWebSocketManager.onMessage('__global__', (data) => {
      console.log("[ProtectedLayout] Mensagem global recebida:");

      // Verificar se é uma notificação de tarefa
      if (data.type === 'task_reminder' || data.type === 'overdue_tasks' ||
          data.type === 'human_handoff' || data.type === 'task_completed') {
        console.log("[ProtectedLayout] Notificação de tarefa detectada:");
        setWebsocketMessage(data);
      }
    });

    // Handler alternativo para todos os tópicos
    const unsubscribeAll = unifiedWebSocketManager.onMessage('*', (data) => {
      console.log("[ProtectedLayout] Mensagem de qualquer tópico:");

      if (data.type === 'task_reminder' || data.type === 'overdue_tasks' ||
          data.type === 'human_handoff' || data.type === 'task_completed') {
        console.log("[ProtectedLayout] Notificação de tarefa via * detectada:");
        setWebsocketMessage(data);
      }
    });

    // Handler para reconexão
    const handleReconnect = () => {
      console.log("WebSocket reconectado");
      // Reconectar se necessário
      if (!unifiedWebSocketManager.isConnected()) {
        unifiedWebSocketManager.connect();
      }
    };

    // Adicionar listener de reconexão
    window.addEventListener('websocket:reconnected', handleReconnect);

    // Cleanup
    return () => {
      unsubscribeGlobal();
      unsubscribeAll();
      window.removeEventListener('websocket:reconnected', handleReconnect);
      unifiedWebSocketManager.disconnect();
    };
  }, []);

  const handleClinicBoxClick = async () => {
    if (!canSwitchClinics) {
      return;
    }

    try {
      if (!showClinicDropdown && userClinics.length === 0) {
        setIsLoadingClinics(true);
        const clinics = await getUserClinics();
        const info = await getClinicInfo();

        const updatedClinics = clinics.map(clinic => {
          if (clinic.clinic_id.toString() === clinicId) {
            return {
              ...clinic,
              logo_url: info.logo_url
            };
          }
          return clinic;
        });

        setUserClinics(updatedClinics.sort((a, b) => a.clinic_id - b.clinic_id));
        setIsLoadingClinics(false);
      }
      setShowClinicDropdown(prev => !prev);
    } catch (error) {
      console.error("Erro ao obter lista de clínicas:");
      setIsLoadingClinics(false);
    }
  };

  const handleSelectClinic = async (selectedId: number) => {
    if (!canSwitchClinics) {
      return;
    }

    try {
      await selectActiveClinic(selectedId);
      messageCacheManager.clearAllCache();
      unifiedWebSocketManager.disconnect();
      localStorage.setItem('clinic_id', selectedId.toString());
      setClinicId(selectedId.toString());

      const info = await getClinicInfo();
      const finalName = info.name_clinic?.trim() || info.name?.trim() || null;
      const finalLogo = info.logo_url?.trim() || null;

      setClinicName(finalName);
      setClinicLogoUrl(finalLogo);

      setUserClinics(prevClinics => prevClinics.map(clinic => {
        if (clinic.clinic_id === selectedId) {
          return {
            ...clinic,
            logo_url: finalLogo
          };
        }
        return clinic;
      }));

      setShowClinicDropdown(false);

      // Recarrega a página para atualizar todos os componentes com a nova clínica
      // As notificações serão carregadas automaticamente no useEffect inicial
      window.location.reload();
    } catch (error) {
      console.error("Erro ao selecionar clínica:");
      alert("Não foi possível selecionar a clínica.");
    }
  };

  const handleWhatsAppConnect = () => {
    setShowWhatsAppModal(false);
    // Navegar para a página de configuração do WhatsApp
    window.location.href = '/canais';
  };

  // Hook para permissões do usuário
  useUserPermissions();

  useEffect(() => {
    let isMounted = true;

    const loadTeamPermissions = async () => {
      try {
        const teamPermissions = await getMyTeamPermissions();
        if (!isMounted) return;
        setUserPermissions(
          teamPermissions.menu_permissions,
          teamPermissions.contact_funnel_stages,
          teamPermissions.menu_permissions_mode,
        );
      } catch (error) {
        console.error("Erro ao carregar permissões da equipe:");
      }
    };

    loadTeamPermissions();

    return () => {
      isMounted = false;
    };
  }, [clinicId]);

  const menuSections = buildSidebarMenuSections(whatsappStatusForMenu, isCompanyRegistrationApprovalEnabled() && session?.isPlatformAdmin === true);

  // Filtra cada seção pelas permissões e remove seções vazias
  const filteredSections = menuSections
    .map(section => ({
      label: section.label,
      icon: section.icon,
      items: filterMenusByPermissions(section.items),
    }))
    .filter(section => section.items.length > 0);

  const isMenuItemActive = (item: MenuItem) => (
    location.pathname === item.path ||
    (item.path === '/canais' && location.pathname === '/whatsapp') ||
    Boolean(item.subItems?.some((subItem) => location.pathname === subItem.path))
  );

  const activeSection = filteredSections.find((section) => (
    section.items.some((item) => isMenuItemActive(item))
  )) || filteredSections[0];

  // Seção derivada da rota atual (label), usada para sincronizar o rail
  const routeSection = filteredSections.find((section) => (
    section.items.some((item) => isMenuItemActive(item))
  ))?.label;

  // Sincroniza a seleção do rail com a seção da rota sempre que a rota muda
  useEffect(() => {
    if (routeSection) {
      setSelectedSection(routeSection);
    }
  }, [location.pathname, routeSection]);

  useEffect(() => {
    if (location.pathname === '/chat') {
      setIsCollapsed(true);
    }
  }, [location.pathname]);

  // Seção exibida no painel: seleção do rail, caindo para a seção da rota
  const displayedSection = filteredSections.find((section: SidebarMenuSection) => section.label === selectedSection) || activeSection;


  // Menus essenciais para mobile (filtrados por permissão)
  const allMobileMenuItems = buildMobileMenuItems(whatsappStatusForMenu);

  // Filtra menus mobile baseado nas permissões
  const mobileMenuItems = filterMenusByPermissions(allMobileMenuItems);

  const renderMenuItem = (item: MenuItem) => {
    const hasActiveSubItem = Boolean(item.subItems?.some((subItem) => location.pathname === subItem.path));
    const isSubmenuVisible = openSubmenu === item.path || hasActiveSubItem || location.pathname === item.path;

    return (
      <li key={item.path}>
        <Link to={item.path} className="block">
          <MenuItem
            icon={item.icon}
            label={item.label}
            isActive={isMenuItemActive(item)}
            isCollapsed={false}
            hasSubItems={Boolean(item.subItems?.length)}
            onClick={(event) => {
              event.preventDefault();
              setOpenSubmenu(openSubmenu === item.path ? null : item.path);
            }}
            isSubmenuOpen={isSubmenuVisible}
            badge={item.badge}
            status={item.status as ConnectionStatus}
            isNew={item.isNew}
            isBeta={item.isBeta}
          />
        </Link>
        {item.subItems && isSubmenuVisible && (
          <ul className={`mb-2 mt-1 space-y-1 border-l pl-2 ${isDark ? 'border-sminy-border/15' : 'border-sminy-border/70'}`}>
            {item.subItems.map((subItem) => (
              <li key={subItem.path}>
                <SubMenuItem
                  label={subItem.label}
                  path={subItem.path}
                  isActive={location.pathname === subItem.path}
                  icon={subItem.icon}
                  isNew={subItem.isNew}
                  isBeta={subItem.isBeta}
                />
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const groupedLabels = displayedSection
    ? Array.from(new Set(displayedSection.items
      .map((item) => item.group)
      .filter((group): group is string => Boolean(group))))
    : [];

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'bg-sminy-navy' : 'bg-sminy-canvas'
      }`}>
      {/* Layout */}
      <div className="flex flex-1 relative">
        {/* Sidebar - rebrand Sminy */}
        <aside
          data-testid="global-navigation"
          className={`fixed left-0 top-0 z-20 hidden h-screen border-r transition-all duration-300 ease-out sm:flex ${
            isCollapsed ? 'w-[68px]' : 'w-[316px]'
          } ${isDark ? 'border-sminy-border/15 bg-sminy-navy' : 'border-sminy-border bg-white'}`}
        >
          {/* Global navigation rail */}
          <div className={`flex h-full w-[68px] shrink-0 flex-col items-center border-r py-3 ${
            isDark ? 'border-sminy-border/15 bg-sminy-navy' : 'border-sminy-border bg-sminy-canvas'
          }`}>
            <Link to="/dashboard" className="mb-3 grid h-11 w-11 place-items-center rounded-xl" title="Sminy.ai">
              <img
                src="/fav-sminy-2026.png"
                className={`h-7 w-7 object-contain ${isDark ? 'brightness-0 invert' : ''}`}
                alt="Sminy.ai"
              />
            </Link>

            <nav aria-label="Seções do menu" className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto px-2">
              {filteredSections.map((section: SidebarMenuSection) => {
                const SectionIcon = section.icon;
                const isSelected = displayedSection?.label === section.label;
                return (
                  <button
                    key={section.label}
                    type="button"
                    title={section.label}
                    aria-label={section.label}
                    aria-pressed={isSelected}
                    onClick={() => {
                      setSelectedSection(section.label);
                      if (section.label === 'Chat') {
                        setIsCollapsed(true);
                        if (location.pathname !== '/chat') {
                          navigate('/chat');
                        }
                        return;
                      }
                      if (isCollapsed) {
                        setIsCollapsed(false);
                      }
                    }}
                    className={`relative grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-all duration-150 ${
                      isSelected
                        ? isDark
                          ? 'bg-sminy-primary/15 text-sminy-primary'
                          : 'bg-sminy-blue-light text-sminy-primary'
                        : isDark
                          ? 'text-sminy-muted hover:bg-sminy-blue-soft/10 hover:text-white'
                          : 'text-sminy-muted hover:bg-white hover:text-sminy-text-primary'
                    }`}
                  >
                    <SectionIcon className="h-5 w-5" />
                    {section.label === 'Configurações' && (
                      <span className={`absolute bottom-1.5 right-1.5 h-2 w-2 rounded-full border-2 ${isDark ? 'border-sminy-navy' : 'border-white'} ${
                        whatsappStatusForMenu === 'connected'
                          ? 'bg-sminy-success'
                          : whatsappStatusForMenu === 'connecting'
                            ? 'bg-amber-500 animate-pulse'
                            : whatsappStatusForMenu === 'qr'
                              ? 'bg-blue-500 animate-pulse'
                              : 'bg-red-500'
                      }`} />
                    )}
                  </button>
                );
              })}
            </nav>

            {isCollapsed && <TaskNotifications websocketMessage={websocketMessage} isCollapsed />}

            <div className={`mt-2 flex w-11 shrink-0 flex-col items-center self-center border-y py-1 ${isDark ? 'border-sminy-border/15' : 'border-sminy-border'}`}>
              <Link
                to="/meu-perfil"
                aria-label="Meu perfil"
                title="Meu perfil"
                className={`grid h-10 w-10 place-items-center rounded-xl transition-colors ${isDark ? 'text-sminy-muted hover:bg-sminy-blue-soft/10 hover:text-white' : 'text-sminy-muted hover:bg-white hover:text-sminy-text-primary'}`}
              >
                {session?.photoUrl && failedPhoto !== session.photoUrl ? (
                  <img src={session.photoUrl} alt="" className="h-6 w-6 rounded-full object-cover" onError={() => setFailedPhoto(session.photoUrl || null)} />
                ) : <UserRound className="h-[18px] w-[18px]" />}
              </Link>
            </div>

            <div className="flex flex-col items-center gap-1 pt-2">
              <button
                onClick={toggleTheme}
                className={`grid h-10 w-10 place-items-center rounded-xl transition-colors ${
                  isDark ? 'text-sminy-muted hover:bg-sminy-blue-soft/10 hover:text-white' : 'text-sminy-muted hover:bg-white hover:text-sminy-text-primary'
                }`}
                title={`Modo ${isDark ? 'claro' : 'escuro'}`}
              >
                {isDark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
              </button>
              <button
                onClick={() => setShowLogoutModal(true)}
                className={`grid h-10 w-10 place-items-center rounded-xl transition-colors ${
                  isDark ? 'text-sminy-muted hover:bg-red-500/10 hover:text-red-400' : 'text-sminy-muted hover:bg-red-50 hover:text-red-600'
                }`}
                title="Sair"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={`grid h-10 w-10 place-items-center rounded-xl transition-colors ${
                  isDark ? 'text-sminy-muted hover:bg-sminy-blue-soft/10 hover:text-white' : 'text-sminy-muted hover:bg-white hover:text-sminy-text-primary'
                }`}
                title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
              >
                {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Contextual panel */}
          {!isCollapsed && (
            <div data-testid="contextual-navigation" className="flex min-w-0 flex-1 flex-col">
              <div className={`relative border-b px-4 py-3 ${isDark ? 'border-sminy-border/15' : 'border-sminy-border'}`}>
                <ClinicSelector
                  isCollapsed={false}
                  clinicLogoUrl={clinicLogoUrl}
                  clinicName={clinicName}
                  clinicId={clinicId}
                  canSwitchClinics={canSwitchClinics}
                  showClinicDropdown={showClinicDropdown}
                  handleClinicBoxClick={handleClinicBoxClick}
                />
                {showClinicDropdown && canSwitchClinics && (
                  <ClinicDropdown
                    isLoadingClinics={isLoadingClinics}
                    userClinics={userClinics}
                    clinicId={clinicId}
                    handleSelectClinic={handleSelectClinic}
                  />
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
                {displayedSection && (
                  <MenuSection label={displayedSection.label} isCollapsed={false}>
                    {displayedSection.items.filter((item) => !item.group).map(renderMenuItem)}
                    {groupedLabels.map((groupLabel) => (
                      <MenuGroup key={groupLabel} label={groupLabel} isCollapsed={false}>
                        {displayedSection.items.filter((item) => item.group === groupLabel).map(renderMenuItem)}
                      </MenuGroup>
                    ))}
                  </MenuSection>
                )}
              </div>

              <TaskNotifications websocketMessage={websocketMessage} isCollapsed={false} />
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className={`
          flex-1 min-w-0 min-h-screen overflow-x-hidden transition-all duration-300
          ${isCollapsed ? 'sm:ml-[68px]' : 'sm:ml-[316px]'}
          pb-20 sm:pb-0
          ${isDark ? 'bg-sminy-navy' : 'bg-sminy-canvas'}
        `}>
          <BillingTopBanner alert={billingAlert} />
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 z-30 sm:hidden ${isDark
        ? 'bg-sminy-navy border-t border-sminy-border/15'
        : 'bg-white border-t border-sminy-border'
        }`}>
        <div className="flex items-center justify-around px-2 py-1">
          <MobileNavItem icon={UserRound} label="Perfil" isActive={location.pathname === '/meu-perfil'} path="/meu-perfil" />
          {mobileMenuItems.map((item) => (
            <MobileNavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              isActive={isMenuItemActive(item)}
              path={item.path}
            // status={item.status} // Removido - status do WhatsApp
            />
          ))}
        </div>
      </nav>

      {/* Modal de confirmação de logout */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => setShowLogoutModal(false)}
      />

      {/* Modal de status do WhatsApp */}
      <WhatsAppStatusModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        status={whatsappConnectionStatus}
        onConnect={handleWhatsAppConnect}
        isMaintenanceMode={isMaintenanceMode}
      />
    </div>
  );
};

export default ProtectedLayout;
